import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  RESET_DESIGN_OPTIONS,
  UPDATE_DESIGN_OPTIONS,
} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {
  selectHasOverlayIdChanged,
  selectHasRevealIdChanged,
  selectOverlayId,
  selectRevealId,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {
  selectOverlays,
  selectReveals,
} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';

export const useOverlay = (isMobile = false, isReveal = false) => {
  const dispatch = useDispatch();
  const fabricDesign = useSelector((s) => s.fabricDesign);
  const fabricTemplate = useSelector((s) => s.fabricTemplate);

  // This should be a state variable!
  let overlays = [];
  let overlayId = '';
  let hasOverlayChanged;

  if (isReveal) {
    overlays = useSelector(selectReveals);
    overlayId = useSelector(selectRevealId);
    hasOverlayChanged = useSelector(selectHasRevealIdChanged);
  } else {
    overlays = useSelector(selectOverlays);
    overlayId = useSelector(selectOverlayId);
    hasOverlayChanged = useSelector(selectHasOverlayIdChanged);
  }

  const overlayIcons = useMemo(
    () =>
      overlays?.map((overlay) => {
        const iconVp9 = overlay?.media.find(({label}) => label === 'icon_vp9')?.url || '';
        return {
          iconVp9,
          defaultSrc: iconVp9,
          iconH265: overlay?.media.find(({label}) => label === 'icon_h265')?.url || '',
          id: overlay.id,
        };
      }),
    // biome-ignore lint/correctness/useExhaustiveDependencies: this needs refactoring, overlays should be state
    [overlays]
  );

  const onOverlayReset = useCallback(() => {
    dispatch({
      type: RESET_DESIGN_OPTIONS,
      payload: [isReveal ? 'revealId' : 'overlayId'],
    });
  }, [isReveal, dispatch]);

  const onOverlayChange = useCallback(
    (icon) => {
      const selected = overlays?.find(({id}) => id === icon.id);
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          ...(isReveal ? {revealId: selected?.id} : {overlayId: selected?.id}),
          premiumFeaturesPristine: false,
          pristineBackground: false,
          pristineEnvelopeDesign: false,
        },
      });
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: refactor!
    [overlays, dispatch, isReveal]
  );

  const selectedOverlay = useMemo(() => {
    const selected = overlays?.find(({id}) => id === overlayId);
    if (selected) {
      const mode = isMobile ? 'mobile' : 'desktop';
      let srcVp9 = selected.media.find(({label}) => label === `${mode}_vp9`).url;
      const iconVp9 = selected.media.find(({label}) => label === 'icon_vp9').url;
      let srcH265 = selected.media.find(({label}) => label === `${mode}_h265`).url;
      const iconH265 = selected.media.find(({label}) => label === 'icon_h265').url;

      if (!srcH265 || !srcVp9) {
        const toOverlay = (acc, {label, url}) => ({...acc, [label]: url});
        const revealById = ({id}) => id === fabricDesign.revealId;
        const overlayById = ({id}) => id === fabricDesign.overlayId;

        if (isReveal) {
          srcH265 = fabricTemplate?.allReveals?.find(revealById)?.media?.reduce(toOverlay)?.srcH265;
          srcVp9 = fabricTemplate?.allReveals?.find(revealById)?.media?.reduce(toOverlay).srcVp9;
        } else {
          srcH265 = fabricTemplate?.allReveals
            ?.find(overlayById)
            ?.media?.reduce(toOverlay)?.srcH265;
          srcVp9 = fabricTemplate?.allReveals?.find(overlayById)?.media?.reduce(toOverlay).srcVp9;
        }
      }

      return {...selected, srcVp9, srcH265, iconVp9, iconH265, isVideo: true, id: overlayId};
    }
    return undefined;
  }, [
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix `overlays`!
    overlays,
    overlayId,
    fabricDesign?.overlayId,
    fabricDesign?.revealId,
    fabricTemplate?.allReveals?.find,
    isMobile,
    isReveal,
  ]);

  return {onOverlayChange, onOverlayReset, overlayIcons, selectedOverlay, hasOverlayChanged};
};
